<template>
  <main id="ordini-page" class="page-container" v-if="$route.name === 'ordini'">
    <div class="table-container-ordini">
      <!-- Toolbar -->
      <div class="ordini-toolbar">
        <!-- [sc-48246] Tre sezioni, non due. "Completati" mostrava gli
             archiviati: erano lo stesso insieme, perché l'API archiviava da sé
             gli ordini pagati e consegnati. Ora "Completati" filtra sugli
             stati reali e gli archiviati stanno sul toggle in fondo, come nei
             preventivi. -->
        <!-- [sc-48258] Bottone e sezioni stanno nello stesso gruppo a
             sinistra: con tre figli diretti lo space-between della toolbar
             spingeva le sezioni al centro. "Aggiorna" resta a destra. -->
        <div class="ordini-toolbar__left">
          <!-- [sc-48258] Ordine di pronta consegna, senza passare dal preventivo. -->
          <router-link to="/home/ordini/nuovo" class="primary-btn">
            <i class="fa-solid fa-plus"></i> Nuovo ordine
          </router-link>
          <div class="ordini-tabs">
            <button
              :class="['tab-btn', { active: section === 'corso' }]"
              @click="switchSection('corso')"
            >
              In corso
            </button>
            <button
              :class="['tab-btn', { active: section === 'completati' }]"
              @click="switchSection('completati')"
            >
              Completati
            </button>
          </div>
        </div>
        <button
          class="refresh-btn"
          :class="{ refreshed }"
          @click="handleRefresh"
          :disabled="refreshing"
          title="Aggiorna"
        >
          <i
            :class="[
              'fa-solid',
              refreshed ? 'fa-check' : 'fa-arrows-rotate',
              { 'fa-spin': refreshing },
            ]"
          ></i>
          <span>{{
            refreshing ? "Aggiorna…" : refreshed ? "Aggiornato!" : "Aggiorna"
          }}</span>
        </button>
      </div>

      <!-- Tabella -->
      <div class="tbl-wrap" :class="{ dimmed: isLoading }">
        <table class="tbl quote-table">
          <thead>
            <tr>
              <th>Cliente</th>
              <th>Da saldare</th>
              <th>Data</th>
              <th>Consegna / Ritiro</th>
              <th>Pagamento</th>
              <th>Spedizione</th>
              <th class="doc-cell">Documenti</th>
              <th>Azioni</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="quote in allQuotes"
              :key="quote.id"
              class="quote-row"
              :class="{ 'quote-row--focus': quote.id === focusRowId }"
              :data-quote-id="quote.id"
            >
              <!-- Cliente con tooltip contatti -->
              <td
                class="client-cell"
                @mouseenter="onCellEnter(quote.id)"
                @mouseleave="onCellLeave(quote.id)"
              >
                <button
                  class="client-trigger"
                  @focus="onCellEnter(quote.id)"
                  @blur="onCellLeave(quote.id)"
                  @click.stop.prevent="toggleTooltip(quote.id)"
                  @touchstart.stop.prevent="onTriggerTouch(quote.id)"
                  @keydown="onTriggerKeydown($event, quote.id)"
                  :aria-expanded="isTooltipVisible(quote.id)"
                  :aria-describedby="'tt-' + quote.id"
                  type="button"
                >
                  {{ quote.Client?.clientName || "—" }}
                  <span class="sr-only">Apri contatti</span>
                </button>
                <transition name="fade">
                  <div
                    v-if="isTooltipVisible(quote.id)"
                    :id="'tt-' + quote.id"
                    role="tooltip"
                    class="email-tooltip"
                    :class="{ 'no-pointer': lockClickId === quote.id }"
                    @click.stop
                    @touchstart.stop
                  >
                    <div class="tooltip-row" v-if="quote?.Client?.email">
                      <span class="label">Email</span>
                      <a :href="'mailto:' + quote.Client.email">{{
                        quote.Client.email
                      }}</a>
                      <button
                        class="copy-btn"
                        @click="copyToClipboard(quote.Client.email, 'email')"
                        title="Copia email"
                      >
                        <i class="fa-regular fa-copy"></i>
                      </button>
                    </div>
                    <div class="tooltip-row" v-if="quote?.Client?.pec">
                      <span class="label">PEC</span>
                      <a :href="'mailto:' + quote.Client.pec">{{
                        quote.Client.pec
                      }}</a>
                      <button
                        class="copy-btn"
                        @click="copyToClipboard(quote.Client.pec, 'pec')"
                        title="Copia PEC"
                      >
                        <i class="fa-regular fa-copy"></i>
                      </button>
                    </div>
                    <div class="tooltip-row" v-if="quote?.Client?.phoneNumber">
                      <span class="label">Telefono</span>
                      {{ quote.Client.phoneNumber }}
                      <button
                        class="copy-btn"
                        @click="
                          copyToClipboard(quote.Client.phoneNumber, 'phone')
                        "
                        title="Copia numero"
                      >
                        <i class="fa-regular fa-copy"></i>
                      </button>
                    </div>
                    <div
                      class="tooltip-row muted"
                      v-if="
                        !quote?.Client?.email &&
                        !quote?.Client?.pec &&
                        !quote?.Client?.phoneNumber
                      "
                    >
                      Nessun contatto disponibile.
                    </div>
                  </div>
                </transition>
              </td>

              <td>
                <!-- [sc-48735] Pagato più del dovuto: un credito, non un
                     importo negativo. -->
                <span
                  v-if="daRestituire(quote.realTotalPrice) > 0"
                  class="da-restituire"
                  title="Il cliente ha pagato più del totale"
                >
                  Da restituire
                  {{ formatPriceToPrint(daRestituire(quote.realTotalPrice)) }}
                </span>
                <template v-else>
                  {{ formatPriceToPrint(daSaldare(quote.realTotalPrice)) }}
                </template>
              </td>
              <td class="date-cell">{{ formatToLocalDate(quote.date) }}</td>
              <td class="address-cell">
                <span
                  v-if="quote.deliveryType === 'ritiro'"
                  class="badge badge--ritiro"
                  >Ritiro</span
                >
                <a
                  v-else-if="quote.location && quote.location.link"
                  :href="quote.location.link"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="custom-link"
                  >{{ formatAddress(quote.location.address) }}</a
                >
                <span v-else>{{
                  formatAddress(quote.location?.address || "—")
                }}</span>
              </td>

              <!-- Pagamento -->
              <td class="state-container">
                <StatusSelect
                  :modelValue="quote.payment_state"
                  :options="PAGAMENTO_OPTIONS"
                  :disabled="quote.payment_state === 'Pagato'"
                  @update:modelValue="
                    (val) => {
                      quote.payment_state = val;
                      onQuoteStateChange(quote, 'payment_state', val);
                    }
                  "
                />
              </td>

              <!-- Spedizione -->
              <td class="state-container">
                <StatusSelect
                  :modelValue="quote.state"
                  :options="spedizioneOptions(quote)"
                  :disabled="isStateSelectDisabled(quote)"
                  @update:modelValue="
                    (val) => {
                      quote.state = val;
                      onQuoteStateChange(quote, 'state', val);
                    }
                  "
                />
              </td>

              <!-- Documenti -->
              <td class="doc-cell">
                <button
                  class="doc-btn doc-btn--pdf"
                  type="button"
                  title="Ordine PDF"
                  @click="openDocumentsModal(quote, 'pdf')"
                >
                  <i class="fa-solid fa-file-pdf"></i>
                  <span>PDF</span>
                </button>
                <button
                  class="doc-btn doc-btn--ddt"
                  type="button"
                  title="Storico DDT"
                  @click="openDocumentsModal(quote, 'ddt')"
                >
                  <i class="fa-solid fa-truck"></i>
                  <span>DDT</span>
                </button>
              </td>

              <!-- Azioni -->
              <td class="actions-cell">
                <router-link
                  :to="`/home/ordini/dettaglio/${quote.id}`"
                  class="icons-button"
                  title="Visualizza dettaglio ordine"
                  aria-label="Visualizza dettaglio ordine"
                >
                  <i class="fa-solid fa-eye"></i>
                </router-link>
                <button
                  v-if="canEditRow"
                  class="icons-button"
                  type="button"
                  title="Gestisci pagamenti"
                  @click="openPaymentModal(quote)"
                >
                  <i class="fa-solid fa-euro"></i>
                </button>
                <button
                  v-if="canEditRow"
                  class="icons-button"
                  type="button"
                  title="Genera DDT"
                  @click="openDdtModal(quote)"
                >
                  <i class="fa-solid fa-truck"></i>
                </button>
                <button
                  class="icons-button"
                  type="button"
                  title="Storico consegne"
                  @click="openHistoryModal(quote)"
                >
                  <i class="fa-solid fa-clock-rotate-left"></i>
                </button>
                <button
                  v-if="canEditRow"
                  class="icons-button"
                  type="button"
                  title="Reimposta a preventivo"
                  @click="openResetModal(quote)"
                >
                  <i class="fa-solid fa-rotate-left"></i>
                </button>
                <button
                  v-if="!showArchived"
                  class="icons-button icons-button--danger"
                  @click="openArchiveModal(quote)"
                  title="Archivia ordine"
                  type="button"
                >
                  <i class="fa-solid fa-trash-can"></i>
                </button>
                <!-- [sc-48246] Verde, non rosso: ripristinare non distrugge
                     nulla. L'icona resta fa-trash-arrow-up perché dice da dove
                     l'ordine sta tornando.
                     Il ripristino è sempre disponibile: prima era negato agli
                     ordini completati perché l'auto-archiviazione li avrebbe
                     rimessi fra gli archiviati al caricamento successivo.
                     Tolto quell'automatismo, negarlo lascerebbe un ordine
                     cestinato per sbaglio bloccato lì per sempre. -->
                <button
                  v-else
                  class="icons-button icons-button--success"
                  @click="openRestoreModal(quote)"
                  title="Ripristina ordine"
                  type="button"
                >
                  <i class="fa-solid fa-trash-arrow-up"></i>
                </button>
              </td>
            </tr>
            <!-- [sc-48246] Una sezione vuota ora è normale, non un errore:
                 il messaggio dice quale, altrimenti sembra che non ci siano
                 ordini affatto. -->
            <tr v-if="allQuotes.length === 0 && !isLoading">
              <td colspan="8" class="no-result">{{ emptySectionMessage }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Paginazione / ricerca -->
      <div class="pagination">
        <div class="pagination-left">
          <div class="search-container">
            <i
              v-if="!searchTerm"
              class="fa-solid fa-magnifying-glass search-icon"
            ></i>
            <input
              type="text"
              v-model="searchTerm"
              @keyup.enter="onSearch"
              placeholder="Cerca ordini..."
              class="search-box"
            />
            <button
              v-if="searchTerm"
              @click="onResetSearch"
              class="clear-search-button"
              :disabled="isLoading"
            >
              <i class="fa-solid fa-times"></i>
            </button>
          </div>

          <!-- [sc-48246] Archiviati: toggle a icona, come nei preventivi.
               Non è una terza tab perché ha meno rilevanza di In corso e
               Completati, e qui in fondo alla pagina lo si trova quando lo si
               cerca senza averlo davanti tutto il tempo. -->
          <label class="archived-toggle tooltip">
            <input
              type="checkbox"
              :checked="section === 'archiviati'"
              @change="
                switchSection($event.target.checked ? 'archiviati' : 'corso')
              "
              class="visually-hidden"
              :disabled="isLoading"
              :aria-label="
                section === 'archiviati'
                  ? 'Torna agli ordini in corso'
                  : 'Mostra ordini archiviati'
              "
            />
            <i
              :class="[
                'fa-solid',
                section === 'archiviati' ? 'fa-file-invoice' : 'fa-box-archive',
                'icons-button',
              ]"
              aria-hidden="true"
            ></i>
            <span class="tooltiptext">
              {{
                section === "archiviati"
                  ? "Torna agli ordini in corso"
                  : "Mostra ordini archiviati"
              }}
            </span>
          </label>
        </div>
        <div class="pagination-right">
          <div class="pagination-info">
            {{ pagination.startItem }}-{{ pagination.endItem }} di
            {{ pagination.totalItems }}
          </div>
          <div class="pagination-controls">
            <button
              class="icons-button"
              @click="goToPage(1)"
              :disabled="isLoading || pagination.currentPage === 1"
            >
              <i class="fa-solid fa-angles-left"></i>
            </button>
            <button
              class="icons-button"
              @click="goToPage(pagination.currentPage - 1)"
              :disabled="isLoading || pagination.currentPage === 1"
            >
              <i class="fa-solid fa-chevron-left"></i>
            </button>
            <button
              v-for="pageNum in visiblePages"
              :key="pageNum"
              @click="goToPage(pageNum)"
              :class="{ active: pagination.currentPage === pageNum }"
              class="pages-button"
            >
              {{ pageNum }}
            </button>
            <button
              class="icons-button"
              @click="goToPage(pagination.currentPage + 1)"
              :disabled="
                isLoading || pagination.currentPage === pagination.totalPages
              "
            >
              <i class="fa-solid fa-chevron-right"></i>
            </button>
            <button
              class="icons-button"
              @click="goToPage(pagination.totalPages)"
              :disabled="
                isLoading || pagination.currentPage === pagination.totalPages
              "
            >
              <i class="fa-solid fa-angles-right"></i>
            </button>
          </div>
        </div>
      </div>

      <!-- MODALE DOCUMENTI -->
      <VueFinalModal
        v-model="showDocumentsModal"
        :clickToClose="true"
        :escToClose="true"
        classes="documents-modal"
        content-class="documents-modal-content"
      >
        <template #default>
          <button
            class="modal__close icons-button"
            @click="closeDocumentsModal"
          >
            <i class="fa-solid fa-xmark"></i>
          </button>
          <h3 class="modal__title">
            {{ docModalSection === "ddt" ? "Storico DDT" : "Documenti ordine" }}
            #{{ currentQuoteId }}
          </h3>

          <template v-if="docModalSection === 'pdf'">
            <transition-group name="card" tag="div" class="documents-grid">
              <button
                key="regen-action"
                type="button"
                class="document-card document-card--action"
                :disabled="regenPdfBusy || !currentQuoteId"
                title="Rigenera il PDF ordine aggiornato: lo scarica e aggiorna il documento in elenco"
                @click="regenerateOrderPdf"
              >
                <span class="card-icon-pdf">
                  <i
                    class="fa-solid"
                    :class="
                      regenPdfBusy ? 'fa-spinner fa-spin' : 'fa-arrows-rotate'
                    "
                    aria-hidden="true"
                  ></i>
                </span>
                <span class="card-body">
                  <span class="card-title">{{
                    regenPdfBusy ? "Rigenerazione…" : "Rigenera PDF"
                  }}</span>
                  <span class="card-subtitle"
                    >Aggiorna il documento e scaricalo</span
                  >
                </span>
              </button>
              <div
                v-for="(doc, i) in filteredDocuments"
                :key="safeDocKey(doc, i)"
                class="document-card"
              >
                <a href="#" class="card-link" @click.prevent="openDoc(doc)">
                  <div class="card-icon-pdf">
                    <i class="fa-solid fa-file-pdf"></i>
                  </div>
                  <div class="card-body">
                    <div class="card-title">{{ formatDocName(doc.name) }}</div>
                    <div class="card-subtitle">
                      Data di creazione: {{ formatToLocalDate(doc.createdAt) }}
                    </div>
                  </div>
                </a>
                <button
                  class="icons-button card-email-btn"
                  :class="{ 'email-sent': emailSentMap[selectedQuote?.id] }"
                  :title="
                    emailSentMap[selectedQuote?.id]
                      ? 'Email già inviata in questa sessione'
                      : 'Invia email al cliente'
                  "
                  @click.stop="askSendQuoteEmail(selectedQuote)"
                >
                  <i class="fa-solid fa-envelope"></i>
                </button>
              </div>
              <div v-if="filteredDocuments.length === 0" class="no-result">
                Nessun documento trovato
              </div>
            </transition-group>
          </template>

          <template v-if="docModalSection === 'ddt'">
            <div class="ddt-history">
              <div v-if="selectedDdtDocuments.length === 0" class="no-result">
                Nessun DDT trovato per questo ordine.
              </div>
              <template v-if="ddtDocumentsByCausale.consegna.length > 0">
                <div class="ddt-history__group-label">
                  Consegne
                  <span class="ddt-history__group-count"
                    >({{ ddtDocumentsByCausale.consegna.length }})</span
                  >
                </div>
                <div
                  v-for="(doc, idx) in ddtDocumentsByCausale.consegna"
                  :key="doc.id"
                  class="ddt-history__row"
                >
                  <a
                    href="#"
                    class="ddt-history__link"
                    @click.prevent="openDoc(doc)"
                  >
                    <i class="fa-solid fa-file-arrow-down"></i>
                    DDT N° {{ doc._ddtId }}
                    <span class="ddt-history__seq">Consegna {{ idx + 1 }}</span>
                  </a>
                  <span class="ddt-history__date">{{
                    formatToLocalDate(doc.createdAt)
                  }}</span>
                </div>
              </template>
              <template v-if="ddtDocumentsByCausale.ritiro.length > 0">
                <div class="ddt-history__group-label">Ritiri</div>
                <div
                  v-for="doc in ddtDocumentsByCausale.ritiro"
                  :key="doc.id"
                  class="ddt-history__row"
                >
                  <a
                    href="#"
                    class="ddt-history__link"
                    @click.prevent="openDoc(doc)"
                  >
                    <i class="fa-solid fa-file-arrow-down"></i>
                    DDT N° {{ doc._ddtId }}
                  </a>
                  <span class="ddt-history__date">{{
                    formatToLocalDate(doc.createdAt)
                  }}</span>
                </div>
              </template>
            </div>
          </template>
        </template>
      </VueFinalModal>

      <!-- MODALE CONFERMA CAMBIO STATO -->
      <VueFinalModal
        v-model="showConfirmModal"
        :clickToClose="!confirmBusy"
        :escToClose="!confirmBusy"
        classes="documents-modal"
        content-class="confirm-modal-content modal--state"
      >
        <template #default>
          <button
            class="modal__close icons-button"
            @click="handleConfirmCancel"
            :disabled="confirmBusy"
            title="Chiudi"
          >
            <i class="fa-solid fa-xmark"></i>
          </button>
          <h3 class="modal__title">{{ confirmTitle }}</h3>
          <p class="modal__subtitle" v-html="confirmMessage"></p>
          <div class="modal-actions">
            <button
              class="secondary-btn"
              @click="handleConfirmCancel"
              :disabled="confirmBusy"
            >
              Annulla
            </button>
            <button
              class="primary-btn confirm-btn"
              @click="handleConfirmOK"
              :disabled="confirmBusy"
            >
              <span v-if="confirmBusy">{{ confirmBusyLabel }}</span>
              <span v-else>{{ confirmLabel }}</span>
            </button>
          </div>
        </template>
      </VueFinalModal>

      <!-- MODALE CONFERMA INVIO EMAIL [sc-48246]
           L'invio partiva al primo click sull'icona busta: nessun modo di
           accorgersi di aver aperto il documento dell'ordine sbagliato prima
           che la mail fosse già dal cliente. Il destinatario è in chiaro nel
           messaggio, che è il dato su cui l'errore si riconosce. -->
      <VueFinalModal
        v-model="showEmailModal"
        :clickToClose="!emailSending"
        :escToClose="!emailSending"
        classes="documents-modal"
        content-class="confirm-modal-content modal--state"
      >
        <template #default>
          <button
            class="modal__close icons-button"
            @click="closeEmailModal"
            :disabled="emailSending"
            title="Chiudi"
          >
            <i class="fa-solid fa-xmark"></i>
          </button>
          <h3 class="modal__title">Invia email al cliente</h3>
          <p class="modal__subtitle" v-html="emailConfirmMessage"></p>
          <div class="modal-actions">
            <button
              class="secondary-btn"
              @click="closeEmailModal"
              :disabled="emailSending"
            >
              Annulla
            </button>
            <button
              class="primary-btn confirm-btn"
              @click="confirmSendQuoteEmail"
              :disabled="emailSending || !emailPendingQuote?.Client?.email"
            >
              <span v-if="emailSending">Invio…</span>
              <span v-else>Invia email</span>
            </button>
          </div>
        </template>
      </VueFinalModal>

      <!-- MODALE ARCHIVIAZIONE -->
      <VueFinalModal
        v-model="showArchiveModal"
        :clickToClose="!archiving"
        :escToClose="!archiving"
        classes="documents-modal"
        content-class="confirm-modal-content modal--archive"
      >
        <template #default>
          <button
            class="modal__close icons-button"
            @click="closeArchiveModal"
            :disabled="archiving"
            title="Chiudi"
          >
            <i class="fa-solid fa-xmark"></i>
          </button>
          <h3 class="modal__title">Archivia ordine</h3>
          <p class="modal__subtitle">
            Archiviare l'ordine
            <strong>#{{ currentQuoteId }}</strong> di
            <strong>{{ selectedQuote?.Client?.clientName }}</strong
            >?
          </p>
          <div class="modal-actions">
            <button
              class="secondary-btn"
              @click="closeArchiveModal"
              :disabled="archiving"
            >
              Annulla
            </button>
            <button
              class="primary-btn confirm-btn"
              @click="confirmArchive"
              :disabled="archiving"
            >
              <span v-if="archiving">Archivia…</span>
              <span v-else>Archivia</span>
            </button>
          </div>
        </template>
      </VueFinalModal>

      <!-- MODALE RIPRISTINO -->
      <VueFinalModal
        v-model="showRestoreModal"
        :clickToClose="!restoring"
        :escToClose="!restoring"
        classes="documents-modal"
        content-class="confirm-modal-content modal--restore"
      >
        <template #default>
          <button
            class="modal__close icons-button"
            @click="closeRestoreModal"
            :disabled="restoring"
            title="Chiudi"
          >
            <i class="fa-solid fa-xmark"></i>
          </button>
          <h3 class="modal__title">Ripristina ordine</h3>
          <p class="modal__subtitle">
            Ripristinare l'ordine
            <strong>#{{ currentQuoteId }}</strong> di
            <strong>{{ selectedQuote?.Client?.clientName }}</strong
            >?
          </p>
          <div class="modal-actions">
            <button
              class="secondary-btn"
              @click="closeRestoreModal"
              :disabled="restoring"
            >
              Annulla
            </button>
            <button
              class="primary-btn confirm-btn"
              @click="confirmRestore"
              :disabled="restoring"
            >
              <span v-if="restoring">Ripristino…</span>
              <span v-else>Ripristina</span>
            </button>
          </div>
        </template>
      </VueFinalModal>

      <!-- MODALE REIMPOSTA A PREVENTIVO -->
      <VueFinalModal
        v-model="showResetModal"
        :clickToClose="!resetting"
        :escToClose="!resetting"
        classes="documents-modal"
        content-class="confirm-modal-content modal--reset"
      >
        <template #default>
          <button
            class="modal__close icons-button"
            @click="closeResetModal"
            :disabled="resetting"
            title="Chiudi"
          >
            <i class="fa-solid fa-xmark"></i>
          </button>
          <h3 class="modal__title">Reimposta a preventivo</h3>
          <p class="modal__subtitle">
            Vuoi reimpostare l'Ordine
            <strong>#{{ currentQuoteId }}</strong> da stato
            <strong>'Confermato'</strong> a stato <strong>'In attesa'</strong>?
          </p>
          <div class="modal-actions">
            <button
              class="secondary-btn"
              @click="closeResetModal"
              :disabled="resetting"
            >
              Annulla
            </button>
            <button
              class="primary-btn confirm-btn"
              @click="confirmReset"
              :disabled="resetting"
            >
              <span v-if="resetting">Reimposto…</span>
              <span v-else>Reimposta</span>
            </button>
          </div>
        </template>
      </VueFinalModal>

      <!-- MODALE GESTISCI PAGAMENTI -->
      <VueFinalModal
        v-model="showDiscountModal"
        :clickToClose="true"
        :escToClose="true"
        classes="documents-modal"
        content-class="documents-modal-content payments-modal-content"
      >
        <template #default>
          <button class="modal__close icons-button" @click="closePaymentModal">
            <i class="fa-solid fa-xmark"></i>
          </button>

          <h3 class="modal__title">
            Gestisci pagamenti – Ordine #{{ currentQuoteId }}
          </h3>

          <div v-if="paymentLoading" class="history-loading">
            <i class="fa-solid fa-spinner fa-spin"></i> Caricamento riepilogo
            pagamenti…
          </div>

          <div v-else class="quote-modifier">
            <div class="wizard-modifier">
              <div class="wiz-panel">
                <!-- Storico pagamenti -->
                <div class="history-section">
                  <div class="history-section__title">Storico pagamenti</div>
                  <div v-if="paymentHistory.length === 0" class="no-result">
                    Nessun pagamento registrato.
                  </div>
                  <table v-else class="history-table">
                    <thead>
                      <tr>
                        <th>Data</th>
                        <th>Tipo</th>
                        <th>Metodo</th>
                        <th>Importo</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="p in paymentHistory" :key="p.id">
                        <td>
                          {{ formatToLocalDate(p.paidAt ?? p.createdAt) }}
                        </td>
                        <td>
                          {{
                            (
                              getPaymentTypes().find(
                                (t) => t.value === p.type
                              ) || {}
                            ).label || p.type
                          }}
                        </td>
                        <td>{{ p.method }}</td>
                        <td>
                          {{ formatPriceToPrint(Number(p.amount) || 0) }}
                        </td>
                      </tr>
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colspan="3"><strong>Totale pagato</strong></td>
                        <td>
                          <strong>{{
                            formatPriceToPrint(
                              paymentSummary?.totalePagato || 0
                            )
                          }}</strong>
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>

                <!-- [sc-48258] Ordine già saldato: lo si dice, invece di
                     lasciare il campo aperto e il bottone spento. -->
                <p v-if="ordineGiaSaldato" class="wiz-alert">
                  <i class="fa-solid fa-circle-check"></i>
                  {{ avvisoPagamento }}
                </p>

                <!-- Tipo pagamento -->
                <div class="payment-type-cards">
                  <button
                    v-for="t in getPaymentTypes()"
                    :key="t.value"
                    type="button"
                    class="payment-type-card"
                    :class="{
                      'payment-type-card--active': paymentType === t.value,
                    }"
                    :disabled="ordineGiaSaldato"
                    @click="onPaymentTypeChange(t.value)"
                  >
                    {{ t.label }}
                  </button>
                </div>

                <!-- Importo -->
                <div class="wiz-optional">
                  <p class="wiz-optional-label">
                    <i class="fa-solid fa-circle-info"></i>
                    Importo pagamento
                  </p>
                  <div class="wiz-input-wrap">
                    <span class="wiz-input-prefix">€</span>
                    <input
                      type="number"
                      v-model.number="paymentAmount"
                      min="0"
                      step="1"
                      placeholder="0"
                      class="wiz-input"
                      :disabled="ordineGiaSaldato"
                    />
                  </div>
                  <p
                    v-if="importoOltreIlRimanente"
                    class="wiz-alert wiz-alert--error"
                  >
                    <i class="fa-solid fa-triangle-exclamation"></i>
                    {{ avvisoPagamento }}
                  </p>
                </div>

                <!-- Metodo di pagamento -->
                <div class="wiz-optional">
                  <p class="wiz-optional-label">
                    <i class="fa-solid fa-credit-card"></i>
                    Metodo di pagamento
                  </p>
                  <div class="wiz-input-wrap wiz-input-wrap--select">
                    <select
                      v-model="paymentMethod"
                      class="wiz-input"
                      :disabled="ordineGiaSaldato"
                    >
                      <option
                        v-for="m in getPaymentMethods()"
                        :key="m"
                        :value="m"
                      >
                        {{ m }}
                      </option>
                    </select>
                  </div>
                </div>

                <!-- Riepilogo -->
                <div class="wiz-preview preview breakdown">
                  <ul class="calc-list">
                    <li>
                      Totale ordine:
                      <strong>{{
                        formatPriceToPrint(
                          paymentSummary ? paymentSummary.totaleDovuto : 0
                        )
                      }}</strong>
                    </li>
                    <li>
                      Totale già pagato:
                      <strong>{{
                        formatPriceToPrint(
                          paymentSummary ? paymentSummary.totalePagato : 0
                        )
                      }}</strong>
                    </li>
                    <li>
                      Tipo pagamento:
                      <strong>{{
                        (
                          getPaymentTypes().find(
                            (t) => t.value === paymentType
                          ) || {}
                        ).label
                      }}</strong>
                    </li>
                    <li
                      v-if="
                        (Number(paymentAmount) || 0) > 0 &&
                        !ordineGiaSaldato &&
                        !importoOltreIlRimanente
                      "
                    >
                      Importo pagamento:
                      <strong
                        >-
                        {{
                          formatPriceToPrint(Number(paymentAmount) || 0)
                        }}</strong
                      >
                    </li>
                    <li class="row-highlight">
                      Rimanente dopo pagamento:
                      <span class="calc-final">
                        <strong>{{
                          formatPriceToPrint(getPaymentRemainingAfterInput())
                        }}</strong>
                      </span>
                    </li>
                  </ul>
                </div>

                <!-- Azioni -->
                <div class="wiz-actions">
                  <button
                    class="wiz-btn"
                    type="button"
                    @click="closePaymentModal"
                  >
                    Annulla
                  </button>
                  <button
                    class="wiz-btn wiz-btn--cta"
                    type="button"
                    :disabled="
                      isPaymentInvalid() ||
                      paymentBusy ||
                      paymentPdfBusy ||
                      paymentLoading
                    "
                    @click="savePayment(selectedQuote)"
                  >
                    <span v-if="paymentBusy || paymentPdfBusy"
                      >Salvataggio e generazione…</span
                    >
                    <span v-else
                      >Salva e genera PDF <i class="fa-solid fa-check"></i
                    ></span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </template>
      </VueFinalModal>

      <!-- MODALE STORICO CONSEGNE -->
      <VueFinalModal
        v-model="showHistoryModal"
        :clickToClose="true"
        :escToClose="true"
        classes="documents-modal"
        content-class="documents-modal-content history-modal-content"
      >
        <template #default>
          <button class="modal__close icons-button" @click="closeHistoryModal">
            <i class="fa-solid fa-xmark"></i>
          </button>
          <h3 class="modal__title">Storico consegne</h3>
          <p class="modal__subtitle">
            Ordine #{{ historyQuote?.id
            }}<template v-if="historyQuote?.Client?.clientName">
              - {{ historyQuote.Client.clientName }}</template
            >
          </p>

          <div v-if="historyLoading" class="history-loading">
            <i class="fa-solid fa-spinner fa-spin"></i> Caricamento…
          </div>

          <template v-else>
            <!-- Consegne DDT -->
            <div class="history-section">
              <div class="history-section__title">Consegne effettuate</div>
              <div v-if="historyDdts.length === 0" class="no-result">
                Nessuna consegna registrata.
              </div>
              <table v-else class="history-table">
                <thead>
                  <tr>
                    <th>N°</th>
                    <th>Data</th>
                    <th>Tipo</th>
                    <th>Documento</th>
                    <th class="text-right">Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="ddt in historyDdts" :key="ddt.id">
                    <td>{{ ddt.id }}</td>
                    <td>{{ formatToLocalDate(ddt.date ?? ddt.createdAt) }}</td>
                    <td>
                      <span
                        :class="[
                          'history-badge',
                          'history-badge--' + ddt.causale,
                        ]"
                      >
                        {{
                          {
                            consegna: "Consegna",
                            ritiro: "Ritiro",
                          }[ddt.causale] ?? ddt.causale
                        }}
                        <template v-if="ddt.sequence">
                          {{ ddt.sequence }}</template
                        >
                      </span>
                    </td>
                    <td>
                      <a
                        v-if="ddt.documentUrl"
                        href="#"
                        class="custom-link"
                        @click.prevent="openDdtDoc(ddt)"
                      >
                        <i class="fa-solid fa-file-arrow-down"></i> DDT_N_{{
                          ddt.id
                        }}.pdf
                      </a>
                      <span v-else class="muted">—</span>
                    </td>
                    <!-- [sc-48496] Si correggono ed eliminano i DDT di consegna
                         e, dalla sc-49066, di ritiro: il reso si aggiorna
                         rigenerandolo. -->
                    <td class="text-right history-actions">
                      <template
                        v-if="['consegna', 'ritiro'].includes(ddt.causale)"
                      >
                        <button
                          type="button"
                          class="icons-button"
                          title="Modifica DDT"
                          :disabled="ddtEditLoadingId === ddt.id"
                          @click="openDdtEdit(ddt)"
                        >
                          <i
                            :class="
                              ddtEditLoadingId === ddt.id
                                ? 'fa-solid fa-spinner fa-spin'
                                : 'fa-solid fa-pen'
                            "
                          ></i>
                        </button>
                        <button
                          type="button"
                          class="icons-button"
                          title="Elimina DDT"
                          @click="askDeleteDdt(ddt)"
                        >
                          <i class="fa-solid fa-trash"></i>
                        </button>
                      </template>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Stato articoli -->
            <div class="history-section">
              <div class="history-section__title">Stato articoli</div>
              <div v-if="historyItems.length === 0" class="no-result">
                Nessun articolo.
              </div>
              <div v-else class="history-items-list">
                <div
                  v-for="item in historyItems"
                  :key="item.id ?? item.name"
                  class="history-item-row"
                >
                  <div class="history-item-header">
                    <span class="history-item-name">{{ item.name }}</span>
                    <span class="history-item-qty">
                      {{ item.delivered }} / {{ item.ordered }}
                    </span>
                    <i
                      v-if="item.remaining <= 0"
                      class="fa-solid fa-check history-check"
                    ></i>
                  </div>
                  <div class="history-progress-track">
                    <div
                      class="history-progress-fill"
                      :class="{ complete: item.remaining <= 0 }"
                      :style="{ width: item.pct + '%' }"
                    ></div>
                  </div>
                  <div class="history-item-pct">{{ item.pct }}%</div>
                </div>
              </div>
            </div>
          </template>
        </template>
      </VueFinalModal>

      <!-- MODALE CONFERMA ELIMINAZIONE DDT [sc-48496] -->
      <VueFinalModal
        v-model="showDdtDeleteModal"
        :clickToClose="!ddtDeleteBusy"
        :escToClose="!ddtDeleteBusy"
        classes="documents-modal"
        content-class="confirm-modal-content modal--state"
      >
        <template #default>
          <button
            class="modal__close icons-button"
            @click="showDdtDeleteModal = false"
            :disabled="ddtDeleteBusy"
            title="Chiudi"
          >
            <i class="fa-solid fa-xmark"></i>
          </button>
          <h3 class="modal__title">
            Eliminare il DDT N° {{ ddtToDelete?.id }}?
          </h3>
          <p class="modal__subtitle">
            Gli articoli del documento tornano da consegnare sull'ordine e, se
            erano stati scaricati con questo DDT, tornano in giacenza. Lo stato
            dell'ordine si aggiorna di conseguenza. L'operazione non si annulla.
          </p>
          <div class="modal-actions">
            <button
              class="secondary-btn"
              @click="showDdtDeleteModal = false"
              :disabled="ddtDeleteBusy"
            >
              Annulla
            </button>
            <button
              class="primary-btn confirm-btn"
              @click="confirmDeleteDdt"
              :disabled="ddtDeleteBusy"
            >
              <span v-if="ddtDeleteBusy">Eliminazione…</span>
              <span v-else>Elimina DDT</span>
            </button>
          </div>
        </template>
      </VueFinalModal>

      <!-- DDT MODAL -->
      <DdtModal
        :show="showDdtModal"
        @update:show="onDdtModalShow"
        :edit-ddt="ddtEdit"
        @updated="onDdtChanged"
        @request-delete="onDdtRequestDelete"
        :quoteId="ddtModalQuoteId"
        :clientId="ddtModalClientId"
        :clientName="ddtModalClientName"
        :items="ddtModalItems"
        :is-partial-delivery="ddtModalIsPartialDelivery"
        :ddt-consegna-count="ddtModalDdtCount"
        :consegna-complete="ddtModalConsegnaComplete"
        :solo-ritiri="ddtModalSoloRitiri"
        @generated="onDdtGenerated"
      />
    </div>
  </main>
  <router-view></router-view>
</template>

<script>
import axiosInstance from "@/axiosInstance";
import { generateQuotePdf } from "@/utilities/generateQuotePdf";
import {
  aggiornaDaSaldare,
  daRestituire,
  daSaldare,
  ordineNonModificabile,
} from "@/utilities/pagamenti";
import { mapActions, mapGetters } from "vuex/dist/vuex.cjs.js";
import {
  formatPriceToPrint,
  formatToLocalDate,
  formatToLocalDateAndHours,
} from "../utilities/formatting";
import DdtModal from "@/components/DdtModal.vue";
import StatusSelect from "@/components/StatusSelect.vue";
import apriDocumento from "@/mixins/apriDocumento";
import { nomeDaUrl } from "@/utilities/openDocument";

const PAGAMENTO_OPTIONS = [
  { value: "In attesa", label: "In attesa", color: "#ffa726" },
  {
    value: "Pagato Parzialmente",
    label: "Pagato Parzialmente",
    color: "#42a5f5",
  },
  { value: "Pagato", label: "Pagato", color: "#4caf50" },
];

const SPEDIZIONE_COLOR = {
  "In magazzino": "#9ca3af",
  "Consegna Parziale": "#fb8c00",
  "In consegna": "#ffa726",
  Consegnato: "#4caf50",
};

export default {
  name: "OrdiniPage",
  mixins: [apriDocumento],
  components: { DdtModal, StatusSelect },

  computed: {
    ...mapGetters({
      allQuotes: "quotes/list",
      pagination: "quotes/pagination",
    }),

    /* [sc-48258] Ordine già saldato: non c'è niente da incassare, quindi la
       modale non deve far scrivere un importo. Prima il campo restava aperto,
       il riepilogo mostrava la riga «- € 1.111,00» come se quei soldi fossero
       in uscita e il bottone era solo disabilitato, senza dire perché.
       La soglia è mezzo centesimo, come in `daSaldare`: il totale righe del
       server ha i millesimi e un ordine saldato per intero resta qualche
       millesimo sopra lo zero. */
    ordineGiaSaldato() {
      if (!this.paymentSummary) return false;
      return Number(this.paymentSummary.rimanente) < 0.005;
    },

    /* Importo più alto del rimanente: il server lo rifiuta comunque
       («Importo superiore al totale dovuto dell'ordine»), ma va detto qui,
       mentre si scrive, non dopo aver premuto Salva. */
    importoOltreIlRimanente() {
      if (!this.paymentSummary || this.ordineGiaSaldato) return false;
      const amount = Number(this.paymentAmount);
      if (!Number.isFinite(amount) || amount <= 0) return false;
      return amount > Number(this.paymentSummary.rimanente);
    },

    avvisoPagamento() {
      if (this.ordineGiaSaldato) {
        return "Ordine già saldato: non ci sono importi da registrare.";
      }
      if (this.importoOltreIlRimanente) {
        return `Importo superiore al rimanente (${this.formatPriceToPrint(
          this.paymentSummary.rimanente
        )}).`;
      }
      return "";
    },

    visiblePages() {
      const range = 2;
      const start = Math.max(1, this.pagination.currentPage - range);
      const end = Math.min(
        this.pagination.totalPages,
        this.pagination.currentPage + range
      );
      return Array.from({ length: end - start + 1 }, (_, i) => start + i);
    },

    // [sc-48246] Le azioni che modificano l'ordine restano nascoste fuori dalle
    // sezioni operative. Un ordine completato è chiuso: l'API rifiuta i cambi
    // di stato, quindi mostrarne i comandi sarebbe una promessa non mantenuta.
    // Resta il cestino, che archiviare un ordine chiuso è legittimo.
    showArchived() {
      return this.section === "archiviati";
    },
    canEditRow() {
      return this.section === "corso";
    },
    emptySectionMessage() {
      if (this.searchTerm) return "Nessun ordine trovato per la ricerca.";
      if (this.section === "completati")
        return "Nessun ordine completato: qui arrivano quelli consegnati e pagati.";
      if (this.section === "archiviati") return "Nessun ordine archiviato.";
      return "Nessun ordine in corso.";
    },

    // [sc-48246] Il destinatario in chiaro: è il dato su cui si riconosce di
    // aver aperto l'ordine sbagliato. Senza indirizzo si avvisa invece di
    // chiedere conferma a vuoto.
    emailConfirmMessage() {
      const q = this.emailPendingQuote;
      if (!q) return "";
      const dest = q?.Client?.email;
      const cliente = q?.Client?.clientName ?? "cliente";
      if (!dest) {
        return (
          `<strong>${cliente}</strong> non ha un indirizzo email in anagrafica: ` +
          `l'invio per l'ordine <strong>#${q.id}</strong> fallirebbe.`
        );
      }
      return (
        `Inviare il PDF dell'ordine <strong>#${q.id}</strong> a ` +
        `<strong>${cliente}</strong>, all'indirizzo <strong>${dest}</strong>?`
      );
    },

    filteredDocuments() {
      const list = Array.isArray(this.selectedDocuments)
        ? this.selectedDocuments
        : [];
      const seen = new Set();
      return list.filter(Boolean).filter((doc) => {
        const k = doc.id ?? doc.name ?? doc.url ?? Math.random();
        if (seen.has(k)) return false;
        seen.add(k);
        return true;
      });
    },

    historyItems() {
      const q = this.historyQuote;
      if (!q) return [];
      const AREA_CATS = [
        "piastrelle",
        "rivestimenti parete",
        "parquet",
        "laminati",
        "decking esterno",
      ];
      const LINEAR_CATS = ["profili", "sistemi di scarico"];
      const rows = [];
      const seen = new Set();
      (q.items || []).forEach((it) => {
        seen.add(it.id);
        const isArea = AREA_CATS.includes((it.category || "").toLowerCase());
        const isLinear = LINEAR_CATS.includes(
          (it.category || "").toLowerCase()
        );
        const uom = it.salesUnit || (isArea ? "SC" : isLinear ? "CONF" : "PZ");
        const ordered = Number(it?.QuoteItem?.quantity ?? 0);
        const delivered = Number(
          this.historyDeliveredQtyMap[String(it.id)] || 0
        );
        const pct =
          ordered > 0
            ? Math.min(100, Math.round((delivered / ordered) * 100))
            : 0;
        rows.push({
          id: it.id,
          name: it.itemName || it.supplierCode || it.itemCode || "—",
          ordered,
          delivered,
          remaining: Math.max(0, ordered - delivered),
          uom,
          pct,
        });
      });
      (q.quote_items || []).forEach((qi) => {
        const it = qi?.item || {};
        if (it.id && seen.has(it.id)) return;
        const isArea = AREA_CATS.includes((it.category || "").toLowerCase());
        const isLinear = LINEAR_CATS.includes(
          (it.category || "").toLowerCase()
        );
        const uom = it.salesUnit || (isArea ? "SC" : isLinear ? "CONF" : "PZ");
        const ordered = Number(qi.quantity ?? 0);
        const delivered = Number(
          this.historyDeliveredQtyMap[String(it.id)] || 0
        );
        const pct =
          ordered > 0
            ? Math.min(100, Math.round((delivered / ordered) * 100))
            : 0;
        rows.push({
          id: it.id,
          name: it.itemName || it.supplierCode || it.itemCode || "—",
          ordered,
          delivered,
          remaining: Math.max(0, ordered - delivered),
          uom,
          pct,
        });
      });
      return rows;
    },

    ddtDocumentsByCausale() {
      const groups = { consegna: [], ritiro: [] };
      for (const doc of this.selectedDdtDocuments) {
        const key = doc._causale in groups ? doc._causale : "consegna";
        groups[key].push(doc);
      }
      const byDate = (a, b) => new Date(a.createdAt) - new Date(b.createdAt);
      return {
        consegna: [...groups.consegna].sort(byDate),
        ritiro: [...groups.ritiro].sort(byDate),
      };
    },
  },

  data() {
    return {
      isLoading: false,
      PAGAMENTO_OPTIONS,
      refreshing: false,
      refreshed: false,
      searchTerm: "",
      // [sc-48246] Sezione visualizzata: "corso" | "completati" | "archiviati".
      // Prima era il booleano showArchived, che non poteva rappresentare tre
      // insiemi — ed è il motivo per cui "Completati" mostrava gli archiviati.
      section: "corso",
      // [sc-49136] riga evidenziata dopo "Vai all'ordine"
      focusRowId: null,
      focusTimer: null,
      currentQuoteId: null,
      selectedQuote: null,

      showDocumentsModal: false,
      docModalSection: "pdf",
      selectedDocuments: [],
      selectedDdtDocuments: [],
      emailSentMap: {},
      regenPdfBusy: false,

      showConfirmModal: false,
      confirmTitle: "",
      confirmMessage: "",
      confirmLabel: "Conferma",
      confirmBusyLabel: "Aggiorno…",
      confirmBusy: false,
      pendingChange: null,

      // [sc-48246] Conferma invio email
      showEmailModal: false,
      emailSending: false,
      emailPendingQuote: null,

      showArchiveModal: false,
      archiving: false,
      showRestoreModal: false,
      restoring: false,
      showResetModal: false,
      resetting: false,

      showDdtModal: false,
      ddtModalItems: [],
      ddtModalQuoteId: null,
      ddtModalClientId: null,
      ddtModalClientName: null,
      ddtModalIsPartialDelivery: false,
      ddtModalDdtCount: 0,
      ddtModalConsegnaComplete: false,
      ddtModalSoloRitiri: false,

      tooltipOpenId: null,
      hoveredId: null,
      hideTimer: null,
      lockClickId: null,

      showDiscountModal: false,
      depositAmount: 0,
      removeModifierBusy: false,

      // Gestione pagamenti (quote_payments)
      paymentType: "acconto",
      paymentAmount: 0,
      paymentMethod: "Contanti",
      paymentSummary: null,
      paymentLoading: false,
      paymentBusy: false,
      paymentPdfBusy: false,
      paymentHistory: [],

      showHistoryModal: false,
      historyQuote: null,
      historyDdts: [],
      historyDeliveredQtyMap: {},
      historyLoading: false,

      // [sc-48496] modifica/eliminazione di un DDT di consegna dallo storico
      ddtEdit: null,
      ddtEditLoadingId: null,
      showDdtDeleteModal: false,
      ddtToDelete: null,
      ddtDeleteBusy: false,
    };
  },

  methods: {
    daRestituire,
    daSaldare,
    ...mapActions([
      "fetchQuotes",
      "archiveQuote",
      "restoreQuote",
      "sendQuoteDocumentEmail",
    ]),

    formatPriceToPrint,
    formatToLocalDate,
    formatToLocalDateAndHours,

    async handleRefresh() {
      this.refreshing = true;
      this.refreshed = false;
      try {
        await this.getQuotes(this.pagination.currentPage);
      } finally {
        this.refreshing = false;
        this.refreshed = true;
        setTimeout(() => {
          this.refreshed = false;
        }, 1500);
      }
    },

    async getQuotes(page = 1, search = this.searchTerm, focusId = null) {
      this.isLoading = true;
      try {
        await this.fetchQuotes({
          page,
          focusId,
          limit: 7,
          search,
          archived: this.showArchived,
          isConfirmed: true,
          // [sc-48246] Il filtro sui completati vale solo fra i non archiviati:
          // negli archiviati stanno insieme ordini chiusi e non, e separarli
          // lì sarebbe una distinzione che a nessuno serve.
          completed: this.showArchived
            ? undefined
            : this.section === "completati",
        });
      } catch (err) {
        if (err?.response?.status === 404 && page !== 1) {
          await this.getQuotes(1, search);
          return;
        }
        if (err?.response?.status !== 404) {
          this.$toast.error(
            err?.response?.data?.message ||
              "Errore nel caricamento degli ordini."
          );
        }
      } finally {
        this.isLoading = false;
      }
    },

    /* [sc-49136] Arrivo da "Vai all'ordine" dei preventivi: la sezione viene
       dalla query, la pagina la sceglie il server (focusId). La riga si porta
       a video e si evidenzia per 10 secondi (durata scelta da Andrea: a tempo,
       non fino al passaggio del mouse, che su tablet non c'è e lascerebbe la
       riga accesa mentre si lavora su un altro ordine); numero e sezione escono
       dall'URL, così un aggiornamento della pagina non la evidenzia di nuovo. */
    async vaiAllOrdine(id) {
      await this.getQuotes(1, "", id);
      this.$router.replace({ path: this.$route.path });
      if (!this.allQuotes.some((q) => q.id === id)) {
        this.$toast.info(`L'ordine #${id} non è in questa sezione.`);
        return;
      }
      this.focusRowId = id;
      await this.$nextTick();
      document
        .querySelector(`tr[data-quote-id="${id}"]`)
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
      clearTimeout(this.focusTimer);
      this.focusTimer = setTimeout(() => (this.focusRowId = null), 10000);
    },

    // [sc-48246] "corso" | "completati" | "archiviati". La ricerca si azzera
    // al cambio sezione: un termine che dava risultati in una può darne zero
    // nell'altra, e la sezione sembrerebbe vuota per un motivo sbagliato.
    async switchSection(section) {
      if (this.isLoading || section === this.section) return;
      this.section = section;
      this.searchTerm = "";
      await this.getQuotes(1);
    },

    async onSearch() {
      await this.getQuotes(1);
    },

    async onResetSearch() {
      this.searchTerm = "";
      await this.getQuotes(1);
    },

    goToPage(page) {
      if (page < 1 || page > this.pagination.totalPages) return;
      this.getQuotes(page);
    },

    spedizioneOptions(quote) {
      const s = quote.state;
      let opts;
      if (s === "In magazzino") {
        opts = [
          { value: "In magazzino", label: "In magazzino", disabled: true },
          {
            value: "Consegna Parziale",
            label: "Consegna Parziale",
            disabled: false,
          },
          { value: "In consegna", label: "In consegna", disabled: false },
        ];
      } else if (s === "Consegna Parziale") {
        opts = [
          {
            value: "Consegna Parziale",
            label: "Consegna Parziale",
            disabled: true,
          },
          { value: "Consegnato", label: "Consegnato", disabled: false },
        ];
      } else if (s === "In consegna") {
        opts = [
          { value: "In consegna", label: "In consegna", disabled: true },
          { value: "Consegnato", label: "Consegnato", disabled: false },
        ];
      } else {
        opts = [
          {
            value: s || "Consegnato",
            label: s || "Consegnato",
            disabled: true,
          },
        ];
      }
      // [sc-49066] Merce uscita solo con ritiri in sede: a DB lo stato resta
      // "Consegna Parziale" / "Consegnato" (stessa logica API), qui si legge
      // "Ritiro Parziale" / "Ritirato". Basta un DDT di consegna per tornare
      // all'etichetta di consegna.
      const soloRitiri = this.isSoloRitiri(quote);
      const ETICHETTE_RITIRO = {
        "Consegna Parziale": "Ritiro Parziale",
        Consegnato: "Ritirato",
      };
      return opts.map((o) => ({
        ...o,
        label: (soloRitiri && ETICHETTE_RITIRO[o.value]) || o.label,
        color: SPEDIZIONE_COLOR[o.value] ?? "#9ca3af",
      }));
    },

    // [sc-49066] Merce uscita solo con ritiri in sede: decide le etichette
    // "Ritiro Parziale"/"Ritirato" della colonna e "Ritirato X/Y" della modale.
    isSoloRitiri(quote) {
      return (
        Number(quote?.ddtRitiroCount) > 0 && !Number(quote?.ddtConsegnaCount)
      );
    },

    isStateSelectDisabled(quote) {
      if (quote.state === "Consegnato") return true;
      if (quote.state === "In magazzino")
        return !["Pagato", "Pagato Parzialmente"].includes(quote.payment_state);
      if (quote.state === "Consegna Parziale") return false;
      return false;
    },

    formatAddress(address) {
      if (!address) return "—";
      const withCap = address.replace(/,?\s+(\d{5}\s)/, "\n$1");
      if (withCap !== address) return withCap;
      return address.replace(/,\s*/, ",\n");
    },

    onQuoteStateChange(quote, field, newValue) {
      this.pendingChange = { id: quote.id, field, newValue };
      const qid = quote?.id ?? "—";
      const clientName = quote?.Client?.clientName ?? "cliente";
      // [sc-48735] Avvisa quando il cambio chiude l'ordine alle modifiche:
      // pagato e con la consegna iniziata, non solo completato.
      const chiude = ordineNonModificabile({
        payment_state: quote?.payment_state,
        state: quote?.state,
        [field]: newValue,
      });
      const warnChiude = (stato) =>
        chiude
          ? ` Confermando <strong>${stato}</strong> non potrai più modificarlo.`
          : "";

      if (field === "payment_state" && newValue === "Pagato Parzialmente") {
        this.confirmTitle = "Conferma pagamento parziale";
        this.confirmMessage =
          `Impostare il pagamento su <strong>Pagato Parzialmente</strong> per l'ordine ` +
          `<strong>#${qid}</strong> di <strong>${clientName}</strong>?`;
        this.confirmLabel = "Stato => Pagato Parzialmente";
        this.confirmBusyLabel = "Imposto…";
      } else if (field === "payment_state" && newValue === "Pagato") {
        const warn = warnChiude("Pagato");
        this.confirmTitle = "Conferma pagamento";
        this.confirmMessage =
          `Impostare il pagamento su <strong>Pagato</strong> per l'ordine ` +
          `<strong>#${qid}</strong> di <strong>${clientName}</strong>?${warn}`;
        this.confirmLabel = "Stato => Pagato";
        this.confirmBusyLabel = "Imposto…";
      } else if (field === "state" && newValue === "Consegna Parziale") {
        this.confirmTitle = "Avvia consegna parziale";
        this.confirmMessage =
          `Avviare la consegna parziale per l'ordine <strong>#${qid}</strong> di ` +
          `<strong>${clientName}</strong>?<br>Potrai generare più DDT di consegna in momenti diversi.` +
          warnChiude("la consegna");
        this.confirmLabel = "Avvia consegna parziale";
        this.confirmBusyLabel = "Aggiorno…";
      } else if (field === "state" && newValue === "In consegna") {
        this.confirmTitle = "Manda in consegna";
        this.confirmMessage =
          `Mandare in consegna l'ordine <strong>#${qid}</strong> di ` +
          `<strong>${clientName}</strong>?<br>Questa azione scaricherà lo stock degli articoli.` +
          warnChiude("la consegna");
        this.confirmLabel = "Manda in consegna";
        this.confirmBusyLabel = "Aggiorno…";
      } else if (field === "state" && newValue === "Consegnato") {
        const warn = warnChiude("Consegnato");
        this.confirmTitle = "Conferma consegna";
        this.confirmMessage =
          `Segnare come <strong>Consegnato</strong> l'ordine ` +
          `<strong>#${qid}</strong> di <strong>${clientName}</strong>?${warn}`;
        this.confirmLabel = "Stato => Consegnato";
        this.confirmBusyLabel = "Aggiorno…";
      } else {
        this.confirmTitle = "Conferma modifica";
        this.confirmMessage = `Confermare la modifica per l'ordine <strong>#${qid}</strong>?`;
        this.confirmLabel = "Conferma";
        this.confirmBusyLabel = "Aggiorno…";
      }
      this.showConfirmModal = true;
    },

    handleConfirmCancel() {
      this.showConfirmModal = false;
      this.pendingChange = null;
      this.getQuotes(this.pagination.currentPage);
    },

    async handleConfirmOK() {
      if (!this.pendingChange) return;
      const { id, field, newValue } = this.pendingChange;
      this.confirmBusy = true;
      try {
        await this.updateQuoteState(id, newValue);
      } finally {
        this.confirmBusy = false;
        this.showConfirmModal = false;
        this.pendingChange = null;
      }
      // Cambio manuale dello stato spedizione (senza generare DDT): apri la
      // modale DDT per generare il documento di trasporto corrispondente.
      if (field === "state") {
        const q = this.allQuotes.find((x) => x.id === id);
        // apri solo se l'aggiornamento è andato a buon fine (stato allineato)
        if (q && q.state === newValue) {
          this.openDdtModal(q);
        }
      }

      // [sc-48246] Pagamento parziale: lo stato da solo non dice quanto è
      // stato incassato. Confermato il cambio, si apre la gestione pagamenti
      // — la stessa dell'icona euro — dove l'importo si registra davvero.
      // Stessa forma del ramo DDT qui sopra: si apre solo se il cambio è
      // andato a buon fine.
      if (field === "payment_state" && newValue === "Pagato Parzialmente") {
        const q = this.allQuotes.find((x) => x.id === id);
        if (q && q.payment_state === newValue) {
          this.openPaymentModal(q);
        }
      }
    },

    async updateQuoteState(quoteId, newState) {
      try {
        const payload = JSON.stringify({ state: newState });
        const response = await this.$axios.patch(
          `quote/state/${quoteId}`,
          payload
        );

        if (response.status === 200 || response.status === 201) {
          const successMap = {
            "Pagato Parzialmente": "Stato aggiornato a 'Pagato Parzialmente'.",
            Pagato: "Stato aggiornato a 'Pagato'.",
            Consegnato: "Stato aggiornato a 'Consegnato'.",
            "In consegna": "Stato aggiornato a 'In consegna'.",
            "Consegna Parziale": "Consegna parziale avviata.",
          };
          this.$toast.success(
            response.data?.message ||
              successMap[newState] ||
              "Stato aggiornato."
          );

          const q = this.allQuotes.find((q) => q.id === quoteId);
          if (q) {
            if (newState === "Pagato" || newState === "Pagato Parzialmente")
              q.payment_state = newState;
            else q.state = newState;
            if (newState === "Consegna Parziale")
              q.ddtConsegnaCount = q.ddtConsegnaCount ?? 0;
            q.ddtRitiroCount = q.ddtRitiroCount ?? 0;
          }

          if (q?.payment_state === "Pagato" && q?.state === "Consegnato") {
            setTimeout(async () => {
              await this.getQuotes(this.pagination.currentPage);
              this.$toast.success("Ordine completato e archiviato.");
            }, 1000);
          }
        }
      } catch (err) {
        const data = err.response?.data ?? {};
        const msg =
          data.message ||
          data.details ||
          data.error ||
          "Errore durante l'aggiornamento.";
        this.$toast.error(msg);
        this.getQuotes(this.pagination.currentPage);
      }
    },

    /* ---- Documenti ---- */
    async openDocumentsModal(quote, section = "pdf") {
      this.currentQuoteId = quote?.id ?? null;
      this.selectedQuote = quote;
      this.docModalSection = section;
      this.selectedDocuments = [];
      this.selectedDdtDocuments = [];
      this.showDocumentsModal = true;

      const quoteDocs = (quote.documents || []).filter(Boolean).map((d) => ({
        ...d,
        _quoteUpdatedAt: quote.updatedAt ?? null,
      }));

      let ddtDocs = [];
      if (quote.id) {
        try {
          const { data } = await axiosInstance.get("/ddt", {
            params: { quoteId: quote.id },
          });
          ddtDocs = (data || [])
            .filter((d) => d.documentUrl)
            .map((d) => ({
              id: `ddt-${d.id}`,
              name: `DDT_N_${d.id}.pdf`,
              url: d.documentUrl,
              objectName: nomeDaUrl(d.documentUrl),
              createdAt: d.date ?? d.createdAt,
              _causale: d.causale,
              _ddtId: d.id,
            }));
        } catch {
          /* ignora errori fetch DDT */
        }
      }
      this.selectedDocuments = quoteDocs;
      this.selectedDdtDocuments = ddtDocs;
    },

    closeDocumentsModal() {
      this.showDocumentsModal = false;
      this.selectedDocuments = [];
      this.selectedDdtDocuments = [];
      this.currentQuoteId = null;
      this.selectedQuote = null;
    },

    // Rigenera il PDF ordine aggiornato (items/sconto/pagamenti correnti):
    // lo scarica sul PC e lo aggiorna tra i documenti dell'ordine.
    async regenerateOrderPdf() {
      const quote = this.selectedQuote;
      if (!quote?.id || this.regenPdfBusy) return;
      this.regenPdfBusy = true;
      try {
        // Dati completi ordine: preferisci il locale se ha già le righe con item,
        // altrimenti ri-fetcha il dettaglio.
        const q = this.allQuotes.find((x) => x.id === quote.id) || quote;
        const needsFetch = !q?.quote_items?.some((qi) => qi?.item);
        let dataForPdf;
        if (needsFetch) {
          const res = await axiosInstance.get("/quote/" + quote.id);
          dataForPdf = res.data?.quote || res.data?.data || res.data;
        } else {
          dataForPdf = { ...q };
        }
        if (!dataForPdf.id) dataForPdf.id = quote.id;
        // Storico pagamenti per il riepilogo (acconti + saldi)
        try {
          const { data } = await axiosInstance.get(
            "quote/" + quote.id + "/payments"
          );
          dataForPdf.payments = data?.payments || dataForPdf.payments || [];
        } catch {
          dataForPdf.payments = dataForPdf.payments || [];
        }
        await generateQuotePdf({
          quote: dataForPdf,
          toast: this.$toast,
          uploadQuoteDocument: (payload) =>
            this.$store.dispatch("uploadQuoteDocument", payload),
          isOrder: true,
          onComplete: () => this.refreshOrderDocuments(quote.id),
        });
      } catch (e) {
        this.$toast.error(
          e?.response?.data?.message ||
            e?.response?.data?.details ||
            "Errore nella rigenerazione del PDF."
        );
      } finally {
        this.regenPdfBusy = false;
      }
    },

    // Ricarica i documenti dell'ordine dopo la rigenerazione, così la card
    // aggiornata compare subito nella modale (e nell'elenco).
    async refreshOrderDocuments(id) {
      try {
        const res = await axiosInstance.get("/quote/" + id);
        const fresh = res.data?.quote || res.data?.data || res.data;
        const docs = (fresh?.documents || []).filter(Boolean).map((d) => ({
          ...d,
          _quoteUpdatedAt: fresh?.updatedAt ?? null,
        }));
        this.selectedDocuments = docs;
        const q = this.allQuotes.find((x) => x.id === id);
        if (q && fresh?.documents) q.documents = fresh.documents;
        if (this.selectedQuote && fresh?.documents)
          this.selectedQuote.documents = fresh.documents;
      } catch {
        /* refresh best-effort: se fallisce, i documenti si aggiornano riaprendo */
      }
    },

    // [sc-48246] L'invio non parte più dal click sull'icona: prima si conferma.
    askSendQuoteEmail(quote) {
      if (!quote) return;
      this.emailPendingQuote = quote;
      this.showEmailModal = true;
    },
    closeEmailModal() {
      if (this.emailSending) return;
      this.showEmailModal = false;
      this.emailPendingQuote = null;
    },
    async confirmSendQuoteEmail() {
      const quote = this.emailPendingQuote;
      if (!quote) return;
      this.emailSending = true;
      try {
        await this.onSendQuoteEmail(quote);
      } finally {
        this.emailSending = false;
        this.showEmailModal = false;
        this.emailPendingQuote = null;
      }
    },

    async onSendQuoteEmail(quote) {
      const doc = (quote?.documents || []).at(-1);
      if (!doc) return;
      try {
        await this.sendQuoteDocumentEmail({
          quoteId: quote.id,
          objectName: doc.name,
        });
        this.emailSentMap = { ...this.emailSentMap, [quote.id]: true };
        this.$toast.success("Email inviata al cliente con successo.");
      } catch (err) {
        this.$toast.error(
          err.response?.data?.error || "Errore durante l'invio dell'email."
        );
      }
    },

    /* ---- DDT ---- */
    openDiscountModal(quote) {
      this.currentQuoteId = quote?.id ?? null;
      this.selectedQuote = quote;
      this.depositAmount = 0;
      this.showDiscountModal = true;
    },
    closeDiscountModal() {
      this.showDiscountModal = false;
      this.selectedQuote = null;
      this.currentQuoteId = null;
      this.depositAmount = 0;
    },

    /* ---- Gestione pagamenti (quote_payments) ---- */
    getPaymentTypes() {
      return [
        { value: "acconto", label: "Acconto" },
        { value: "saldo_parziale", label: "Saldo parziale" },
        { value: "saldo_finale", label: "Saldo finale" },
      ];
    },
    getPaymentMethods() {
      return ["Contanti", "Bonifico", "Carta di credito"];
    },
    openPaymentModal(quote) {
      this.currentQuoteId = quote?.id ?? null;
      this.selectedQuote = quote;
      // reset stato nuovo
      this.paymentType = "acconto";
      this.paymentAmount = 0;
      this.paymentMethod = "Contanti";
      this.paymentSummary = null;
      this.paymentHistory = [];
      // per ora riusiamo ancora la vecchia modale, il template lo cambiamo dopo
      this.showDiscountModal = true;
      if (quote?.id != null) this.loadPaymentSummary(quote.id);
    },
    closePaymentModal() {
      this.showDiscountModal = false;
      this.selectedQuote = null;
      this.currentQuoteId = null;
      this.paymentType = "acconto";
      this.paymentAmount = 0;
      this.paymentMethod = "Contanti";
      this.paymentSummary = null;
      this.paymentHistory = [];
    },
    async loadPaymentSummary(id) {
      this.paymentLoading = true;
      try {
        const { data } = await axiosInstance.get("quote/" + id + "/payments");
        this.paymentSummary = {
          totaleDovuto: Number(data?.totaleDovuto) || 0,
          totalePagato: Number(data?.totalePagato) || 0,
          rimanente: Number(data?.rimanente) || 0,
        };
        this.paymentHistory = data?.payments || [];
      } catch (e) {
        this.paymentSummary = null;
        this.paymentHistory = [];
        this.$toast.error(
          e?.response?.data?.message ||
            "Errore nel caricamento del riepilogo pagamenti."
        );
      } finally {
        this.paymentLoading = false;
      }
    },
    onPaymentTypeChange(type) {
      this.paymentType = type;
      if (type === "saldo_finale" && this.paymentSummary) {
        this.paymentAmount = this.paymentSummary.rimanente;
      }
    },
    getPaymentRemainingAfterInput() {
      return Math.max(
        0,
        (this.paymentSummary?.rimanente || 0) -
          (Number(this.paymentAmount) || 0)
      );
    },
    isPaymentInvalid() {
      const amount = Number(this.paymentAmount);
      return (
        this.paymentLoading ||
        this.paymentBusy ||
        this.paymentSummary === null ||
        !Number.isFinite(amount) ||
        amount <= 0 ||
        amount > this.paymentSummary.rimanente
      );
    },
    async savePayment(quote) {
      if (!quote?.id || this.isPaymentInvalid()) return;
      this.paymentBusy = true;
      let saved = false;
      try {
        const { data } = await axiosInstance.post(
          "quote/" + quote.id + "/payments",
          {
            type: this.paymentType,
            amount: Number(this.paymentAmount),
            method: this.paymentMethod,
          }
        );

        // aggiorna localmente la quote in elenco
        const q = this.allQuotes.find((x) => x.id === quote.id);
        // [sc-48735] col pagato si aggiorna anche «Da saldare»
        if (q) {
          aggiornaDaSaldare(q, data.deposit);
          q.payment_state = data.payment_state;
        }
        // aggiorna anche l'oggetto quote passato, se diverso
        if (quote && quote !== q) {
          aggiornaDaSaldare(quote, data.deposit);
          quote.payment_state = data.payment_state;
        }

        // aggiorna riepilogo
        this.paymentSummary = {
          totaleDovuto: Number(data.totaleDovuto) || 0,
          totalePagato: Number(data.totalePagato) || 0,
          rimanente: Number(data.rimanente) || 0,
        };
        if (data.payment) this.paymentHistory.push(data.payment);

        this.$toast.success(data.message || "Pagamento registrato.");
        saved = true;
      } catch (e) {
        this.$toast.error(
          e?.response?.data?.message ||
            e?.response?.data?.details ||
            e?.response?.data?.error ||
            "Errore nel salvataggio del pagamento."
        );
      } finally {
        this.paymentBusy = false;
      }

      // se il pagamento non è stato salvato: niente PDF, modale aperta
      if (!saved) return;

      // pagamento salvato: genera il PDF ordine (nessun rollback se fallisce)
      this.paymentPdfBusy = true;
      try {
        // Stessa logica del vecchio applyDeposit: il GET /quote/:id è un wrapper
        // e non contiene tutti i campi del PDF, quindi si preferisce il quote
        // locale (già aggiornato con deposit/payment_state dal POST).
        const q = this.allQuotes.find((x) => x.id === quote.id);
        const needsFetch = !q?.quote_items?.some((qi) => qi?.item);
        let dataForPdf;
        if (needsFetch) {
          const res = await axiosInstance.get("/quote/" + quote.id);
          dataForPdf = res.data?.quote || res.data?.data || res.data;
        } else {
          dataForPdf = { ...q };
        }
        // id sempre valido (necessario per filename e upload)
        if (!dataForPdf.id) dataForPdf.id = quote.id;
        // payments non incluso da lista/dettaglio: usa lo storico già caricato
        if (!dataForPdf.payments)
          dataForPdf.payments = this.paymentHistory || [];
        if (!dataForPdf.id) {
          throw new Error("Impossibile generare il PDF: id ordine mancante.");
        }
        // Nome descrittivo/univoco dal pagamento appena registrato: il PDF
        // entra nello storico come documento distinto e non sovrascrive
        // "Ordine_N_<id>.pdf".
        const filenameOverride = this.buildPaymentFilename(
          dataForPdf.payments || this.paymentHistory
        );
        await generateQuotePdf({
          quote: dataForPdf,
          toast: this.$toast,
          uploadQuoteDocument: (payload) =>
            this.$store.dispatch("uploadQuoteDocument", payload),
          isOrder: true,
          filenameOverride,
        });
        // chiudi la modale solo dopo la generazione PDF riuscita
        this.closePaymentModal();
      } catch (e) {
        this.$toast.error(
          e?.response?.data?.message ||
            e?.response?.data?.details ||
            e?.response?.data?.error ||
            "Pagamento salvato, ma errore nella generazione del PDF."
        );
      } finally {
        this.paymentPdfBusy = false;
      }
    },

    async applyDeposit(quote) {
      const round2 = (n) =>
        Math.round((Number(n) + Number.EPSILON) * 100) / 100;
      const amount = round2(Number(this.depositAmount));
      if (!amount) return;
      this.confirmBusy = true;
      try {
        await axiosInstance.post(`quote/priceAdjustment/${quote.id}`, {
          type: "deposit",
          amount,
        });
        const q = this.allQuotes.find((x) => x.id === quote.id);
        if (q) q.deposit = round2((Number(q.deposit) || 0) + amount);
        this.$toast.success("Acconto registrato.");
        const needsFetch = !q?.quote_items?.some((qi) => qi?.item);
        const dataForPdf = needsFetch
          ? await axiosInstance.get(`/quote/${quote.id}`).then((r) => r.data)
          : q;
        // Flusso legacy "acconto": pagamento sintetico per ottenere un nome
        // descrittivo/univoco senza sovrascrivere "Ordine_N_<id>.pdf".
        const filenameOverride = this.buildPaymentFilename([
          { type: "acconto", paidAt: new Date().toISOString() },
        ]);
        await generateQuotePdf({
          quote: dataForPdf,
          toast: this.$toast,
          uploadQuoteDocument: (payload) =>
            this.$store.dispatch("uploadQuoteDocument", payload),
          isOrder: true,
          filenameOverride,
        });
        this.closeDiscountModal();
      } catch (e) {
        this.$toast.error(
          e?.response?.data?.message ||
            e?.response?.data?.details ||
            "Errore nel salvataggio."
        );
      } finally {
        this.confirmBusy = false;
      }
    },
    async removeModifier(quote, type) {
      this.removeModifierBusy = true;
      try {
        await axiosInstance.delete(`quote/priceAdjustment/${quote.id}`, {
          data: { type },
        });
        const q = this.allQuotes.find((x) => x.id === quote.id);
        if (q && type === "deposit") q.deposit = 0;
        this.$toast.success("Acconto rimosso.");
      } catch (e) {
        this.$toast.error(
          e?.response?.data?.message || "Errore nella rimozione."
        );
      } finally {
        this.removeModifierBusy = false;
      }
    },

    async openHistoryModal(quote) {
      this.historyQuote = quote;
      this.historyDdts = [];
      this.historyDeliveredQtyMap = {};
      this.historyLoading = true;
      this.showHistoryModal = true;
      try {
        const [ddtRes, deliveredRes] = await Promise.allSettled([
          axiosInstance.get("/ddt", { params: { quoteId: quote.id } }),
          this.$axios.get(`ddt/quote/${quote.id}/delivered-qty`),
        ]);
        if (ddtRes.status === "fulfilled") {
          this.historyDdts = (ddtRes.value.data || []).sort(
            (a, b) =>
              new Date(a.date ?? a.createdAt) - new Date(b.date ?? b.createdAt)
          );
        }
        if (deliveredRes.status === "fulfilled") {
          this.historyDeliveredQtyMap = Object.fromEntries(
            (deliveredRes.value.data || []).map((r) => [
              String(r.itemId),
              Number(r.totalDelivered),
            ])
          );
        }
      } finally {
        this.historyLoading = false;
      }
    },

    closeHistoryModal() {
      this.showHistoryModal = false;
      this.historyQuote = null;
      this.historyDdts = [];
      this.historyDeliveredQtyMap = {};
    },

    async openDdtModal(quote) {
      this.ddtEdit = null; // [sc-48496] la modale torna in creazione
      const AREA_CATS = [
        "piastrelle",
        "rivestimenti parete",
        "parquet",
        "laminati",
        "decking esterno",
      ];
      const LINEAR_CATS = ["profili", "sistemi di scarico"];
      const rows = [];
      const seen = new Set();

      (quote.items || []).forEach((it) => {
        seen.add(it.id);
        const isArea = AREA_CATS.includes((it.category || "").toLowerCase());
        const isLinear = LINEAR_CATS.includes(
          (it.category || "").toLowerCase()
        );
        const uom = it.salesUnit || (isArea ? "SC" : isLinear ? "CONF" : "PZ");
        rows.push({
          itemId: it.id,
          description: it.itemName || it.supplierCode || it.itemCode || "—",
          quantity: it?.QuoteItem?.quantity ?? 0,
          uom,
          deliveredQty: 0,
        });
      });

      (quote.quote_items || []).forEach((qi) => {
        if (qi?.item?.id && seen.has(qi.item.id)) return;
        const it = qi.item || {};
        const isArea = AREA_CATS.includes((it.category || "").toLowerCase());
        const isLinear = LINEAR_CATS.includes(
          (it.category || "").toLowerCase()
        );
        const uom = it.salesUnit || (isArea ? "SC" : isLinear ? "CONF" : "PZ");
        rows.push({
          itemId: it.id || null,
          description: it.itemName || it.supplierCode || it.itemCode || "—",
          quantity: qi.quantity ?? 0,
          uom,
          deliveredQty: 0,
        });
      });

      const isPartial = quote.state === "Consegna Parziale";
      const ddtCount = Number(quote.ddtConsegnaCount) || 0;

      // [sc-49066] Il già consegnato si legge sempre: prima solo se c'era un DDT
      // di consegna, e dopo un ritiro la modale riproponeva tutte le quantità —
      // la stessa merce è uscita due volte (ordine #7 su staging). Il server
      // ora rifiuta comunque le quantità oltre il residuo.
      try {
        const { data: deliveredQtys } = await this.$axios.get(
          `ddt/quote/${quote.id}/delivered-qty`
        );
        const deliveredMap = Object.fromEntries(
          deliveredQtys.map((r) => [String(r.itemId), Number(r.totalDelivered)])
        );
        const updatedRows = rows.map((row) => {
          const delivered =
            row.itemId != null ? deliveredMap[String(row.itemId)] || 0 : 0;
          return {
            ...row,
            deliveredQty: delivered,
            quantity: Math.max(0, Number(row.quantity) - delivered),
          };
        });
        rows.splice(0, rows.length, ...updatedRows);
      } catch (e) {
        console.error("delivered-qty error", e);
      }

      this.ddtModalItems = rows;
      this.ddtModalQuoteId = quote.id;
      this.ddtModalClientId = quote.clientId || null;
      this.ddtModalClientName = quote?.Client?.clientName || null;
      this.ddtModalIsPartialDelivery = isPartial;
      this.ddtModalDdtCount = ddtCount;
      this.ddtModalConsegnaComplete = false;
      this.ddtModalSoloRitiri = this.isSoloRitiri(quote);
      await this.$nextTick();
      this.showDdtModal = true;
    },

    onDdtModalShow(val) {
      this.showDdtModal = val;
      if (!val) this.ddtEdit = null;
    },

    /* [sc-48496] Modifica di un DDT di consegna: la stessa modale della
       generazione, con le righe del DDT al posto di quelle dell'ordine. */
    async openDdtEdit(ddt) {
      this.ddtEditLoadingId = ddt.id;
      try {
        const { data } = await axiosInstance.get(`/ddt/${ddt.id}`);
        this.ddtEdit = data;
        this.ddtModalQuoteId = data.quoteId;
        this.ddtModalClientId = data.clientId || null;
        this.ddtModalClientName = this.historyQuote?.Client?.clientName || null;
        this.ddtModalItems = [];
        await this.$nextTick();
        this.showDdtModal = true;
      } catch (e) {
        this.$toast.error(
          e?.response?.data?.message || "Impossibile aprire il DDT."
        );
      } finally {
        this.ddtEditLoadingId = null;
      }
    },

    // Dalla modale di modifica con tutte le righe tolte: si chiude la modifica
    // e si passa alla stessa conferma del cestino.
    onDdtRequestDelete(ddt) {
      this.onDdtModalShow(false);
      this.askDeleteDdt(ddt);
    },

    askDeleteDdt(ddt) {
      this.ddtToDelete = ddt;
      this.showDdtDeleteModal = true;
    },

    async confirmDeleteDdt() {
      if (!this.ddtToDelete) return;
      this.ddtDeleteBusy = true;
      try {
        await axiosInstance.delete(`/ddt/${this.ddtToDelete.id}`);
        this.$toast.success(`DDT N° ${this.ddtToDelete.id} eliminato.`);
        this.showDdtDeleteModal = false;
        this.ddtToDelete = null;
        await this.onDdtChanged();
      } catch (e) {
        this.$toast.error(
          e?.response?.data?.message || "Errore nell'eliminazione del DDT."
        );
      } finally {
        this.ddtDeleteBusy = false;
      }
    },

    // Dopo modifica o eliminazione l'api ha già riallineato giacenza e stato
    // dell'ordine: si ricaricano l'elenco e lo storico aperto.
    async onDdtChanged() {
      const quoteId = this.historyQuote?.id;
      await this.getQuotes(this.pagination.currentPage);
      if (!quoteId || !this.showHistoryModal) return;
      const q = this.allQuotes.find((x) => x.id === quoteId);
      await this.openHistoryModal(q || this.historyQuote);
    },

    async onDdtGenerated({ ddt, isLastDelivery }) {
      const q = this.allQuotes.find((q) => q.id === ddt.quoteId);
      // [sc-49066] Il contatore giusto per la causale: un ritiro non è una
      // consegna, e "Ritirato" si decide dai due contatori.
      if (q) {
        const key =
          ddt.causale === "ritiro" ? "ddtRitiroCount" : "ddtConsegnaCount";
        q[key] = (Number(q[key]) || 0) + 1;
      }
      if (!ddt.quoteId) return;
      if (isLastDelivery) {
        // Tutti gli articoli consegnati → forziamo Consegnato (il backend
        // imposta da solo solo "Consegna Parziale", non il completamento).
        try {
          await this.updateQuoteState(ddt.quoteId, "Consegnato");
        } catch {
          this.$toast.error("DDT generato ma errore nell'aggiornamento stato.");
        }
      } else if (q && q.state !== "Consegnato") {
        // Consegna parziale: il backend ha GIÀ impostato "Consegna Parziale"
        // alla creazione del DDT. Rispecchiamo lo stato in locale (reattivo,
        // senza refresh) evitando un PATCH ridondante che darebbe errore
        // "ordine già in consegna parziale".
        q.state = "Consegna Parziale";
      }
    },

    /* ---- Archivio ---- */
    openArchiveModal(quote) {
      this.currentQuoteId = quote?.id ?? null;
      this.selectedQuote = quote;
      this.showArchiveModal = true;
    },
    closeArchiveModal() {
      if (this.archiving) return;
      this.showArchiveModal = false;
      this.selectedQuote = null;
      this.currentQuoteId = null;
    },
    async confirmArchive() {
      if (!this.selectedQuote) return;
      this.archiving = true;
      try {
        const response = await this.archiveQuote(this.selectedQuote.id);
        this.$toast.success(
          response?.data?.message || "Ordine archiviato con successo."
        );
        this.showArchiveModal = false;
        this.currentQuoteId = null;
        await this.getQuotes(this.pagination.currentPage);
      } catch (err) {
        this.$toast.error(
          err.response?.data?.details || "Impossibile archiviare l'ordine."
        );
      } finally {
        this.archiving = false;
        this.selectedQuote = null;
      }
    },

    openRestoreModal(quote) {
      this.currentQuoteId = quote?.id ?? null;
      this.selectedQuote = quote;
      this.showRestoreModal = true;
    },
    closeRestoreModal() {
      if (this.restoring) return;
      this.showRestoreModal = false;
      this.selectedQuote = null;
      this.currentQuoteId = null;
    },
    async confirmRestore() {
      if (!this.selectedQuote) return;
      this.restoring = true;
      try {
        const response = await this.restoreQuote(this.selectedQuote.id);
        this.$toast.success(
          response?.data?.message || "Ordine ripristinato con successo."
        );
        this.showRestoreModal = false;
        this.currentQuoteId = null;
        await this.getQuotes(this.pagination.currentPage);
      } catch (err) {
        this.$toast.error(
          err.response?.data?.message || "Impossibile ripristinare l'ordine."
        );
      } finally {
        this.restoring = false;
        this.selectedQuote = null;
      }
    },

    openResetModal(quote) {
      this.currentQuoteId = quote?.id ?? null;
      this.selectedQuote = quote;
      this.showResetModal = true;
    },
    closeResetModal() {
      if (this.resetting) return;
      this.showResetModal = false;
      this.selectedQuote = null;
      this.currentQuoteId = null;
    },
    async confirmReset() {
      if (!this.selectedQuote) return;
      this.resetting = true;
      try {
        await this.updateQuoteState(this.selectedQuote.id, false);
        this.$toast.success("Ordine reimpostato a preventivo.");
        this.showResetModal = false;
        this.currentQuoteId = null;
        await this.getQuotes(this.pagination.currentPage);
      } catch (err) {
        this.$toast.error(
          err.response?.data?.message ||
            "Impossibile reimpostare l'ordine a preventivo."
        );
      } finally {
        this.resetting = false;
        this.selectedQuote = null;
      }
    },

    // [sc-48246] `isCompleted` e `canRestore` sono state rimosse: servivano
    // solo a negare il ripristino agli ordini completati, e quel divieto
    // esisteva perché l'auto-archiviazione li avrebbe rimessi fra gli
    // archiviati al caricamento successivo. Ora dagli archiviati si torna
    // sempre indietro. La disabilitazione dei select di stato non passava di
    // qui: `isStateSelectDisabled` guarda `quote.state` direttamente.

    /* ---- Tooltip cliente ---- */
    isTooltipVisible(id) {
      return this.tooltipOpenId === id || this.hoveredId === id;
    },
    applyTooltipPlacement(id) {
      const tip = document.getElementById("tt-" + id);
      if (tip) tip.dataset.placement = "over";
    },
    onCellEnter(id) {
      clearTimeout(this.hideTimer);
      this.hoveredId = id;
      this.$nextTick(() => this.applyTooltipPlacement(id));
    },
    onCellLeave(id) {
      clearTimeout(this.hideTimer);
      this.hideTimer = setTimeout(() => {
        if (this.tooltipOpenId !== id) this.hoveredId = null;
      }, 120);
    },
    toggleTooltip(id) {
      this.tooltipOpenId = this.tooltipOpenId === id ? null : id;
      if (this.tooltipOpenId === id) {
        this.hoveredId = id;
        this.lockClickId = id;
        setTimeout(() => {
          if (this.lockClickId === id) this.lockClickId = null;
        }, 250);
        this.$nextTick(() => this.applyTooltipPlacement(id));
      }
    },
    onTriggerTouch(id) {
      this.tooltipOpenId = this.tooltipOpenId === id ? null : id;
      if (this.tooltipOpenId === id) {
        this.hoveredId = id;
        this.lockClickId = id;
        setTimeout(() => {
          if (this.lockClickId === id) this.lockClickId = null;
        }, 250);
        this.$nextTick(() => this.applyTooltipPlacement(id));
      }
    },
    onTriggerKeydown(e, id) {
      if (e.key === "Escape") {
        this.tooltipOpenId = null;
        this.hoveredId = null;
      } else if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        this.toggleTooltip(id);
      }
    },
    handleDocumentClick(e) {
      if (!e.target.closest(".client-cell")) this.tooltipOpenId = null;
    },

    async copyToClipboard(text, kind = null) {
      const value = String(text ?? "").trim();
      if (!kind) {
        if (/^\+?[\d()\s.-]{5,}$/.test(value)) kind = "phone";
        else if (/@/.test(value)) kind = "email";
        else kind = "text";
      }
      try {
        await navigator.clipboard.writeText(value);
        const msgMap = {
          phone: "Numero copiato",
          email: "Email copiata",
          pec: "PEC copiata",
          text: "Testo copiato",
        };
        this.$toast?.success?.(`${msgMap[kind] || msgMap.text} negli appunti`);
      } catch {
        this.$toast?.error?.("Impossibile copiare negli appunti");
      }
    },

    safeDocKey(doc, i) {
      if (doc && doc.id != null) return "doc-" + String(doc.id);
      if (doc && doc.url) return "url-" + doc.url;
      if (doc && doc.name) return "name-" + doc.name;
      return "idx-" + String(i);
    },

    // Costruisce il filename descrittivo e univoco di un PDF pagamento a partire
    // dall'ultimo pagamento della lista: "tipologia_YYYY-MM-DD_HH-mm.pdf".
    // Data preferita: paidAt → createdAt → ora corrente.
    buildPaymentFilename(payments) {
      const list = [...(payments || [])];
      if (!list.length) return null;
      const last = list.sort((a, b) => {
        const da = new Date(a?.paidAt ?? a?.createdAt ?? 0).getTime();
        const db = new Date(b?.paidAt ?? b?.createdAt ?? 0).getTime();
        if (da !== db) return da - db;
        return (Number(a?.id) || 0) - (Number(b?.id) || 0);
      })[list.length - 1];

      const TYPE_SLUG = {
        acconto: "acconto",
        saldo_parziale: "saldo-parziale",
        saldo_finale: "saldo-finale",
      };
      const slug = TYPE_SLUG[last?.type] || "pagamento";

      const d = new Date(last?.paidAt ?? last?.createdAt ?? Date.now());
      const pad = (n) => String(n).padStart(2, "0");
      const stamp =
        `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
        `_${pad(d.getHours())}-${pad(d.getMinutes())}`;

      return `${slug}_${stamp}.pdf`;
    },

    // In pagina Ordini il documento ordine principale è presentato come "Ordine"
    // anche se il file salvato si chiama ancora "Preventivo_N_X" (nessun rename
    // retroattivo). I PDF pagamento sono resi in forma leggibile.
    formatDocName(name) {
      const raw = (name ?? "").toString().replace(/\.dev(?=\.pdf$)/i, "");

      const PAY_LABELS = {
        acconto: "Acconto",
        "saldo-parziale": "Saldo parziale",
        "saldo-finale": "Saldo finale",
      };
      const m = raw.match(
        /^(acconto|saldo-parziale|saldo-finale)_(\d{4})-(\d{2})-(\d{2})_(\d{2})-(\d{2})\.pdf$/i
      );
      if (m) {
        const [, type, Y, Mo, D, H, Mi] = m;
        return `${PAY_LABELS[type.toLowerCase()]} - ${D}/${Mo}/${Y} ${H}:${Mi}`;
      }

      return raw.replace(/_N_\d+\.pdf$/i, "").replace(/^Preventivo/i, "Ordine");
    },
  },

  watch: {
    // Ricarica quando si torna su Ordini da un'altra rotta (es. la modifica di
    // un ordine). [sc-49136] Non quando cambia solo la query sulla stessa
    // pagina: `vaiAllOrdine` toglie `evidenzia`/`sezione` con un replace, e
    // ricaricare la pagina 1 cancellava l'ordine appena portato a video se
    // stava in un'altra pagina.
    $route(to, from) {
      if (to.name === "ordini" && from.name !== "ordini") {
        this.getQuotes(1);
      }
    },
    searchTerm() {
      this.onSearch();
    },
  },

  mounted() {
    document.addEventListener("click", this.handleDocumentClick);
    // [sc-49136] "Vai all'ordine" dai preventivi: ?evidenzia=<id>&sezione=…
    const { evidenzia, sezione } = this.$route.query;
    if (["corso", "completati", "archiviati"].includes(sezione)) {
      this.section = sezione;
    }
    const focusId = Number(evidenzia);
    if (Number.isInteger(focusId) && focusId > 0) {
      this.vaiAllOrdine(focusId);
    } else {
      this.getQuotes(1);
    }
  },
  beforeUnmount() {
    document.removeEventListener("click", this.handleDocumentClick);
    clearTimeout(this.focusTimer);
  },
};
</script>

<style src="@/css/OrdiniPage.css" />
